import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Form, Steps, Button, Progress, Tag, Alert, Spin, message, Modal, notification } from 'antd';
import {
  UserOutlined,
  HomeOutlined,
  SolutionOutlined,
  FileProtectOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  SaveOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

// Section Components
import Section01Personal from './Sections/Section01Personal';
import Section02Contact from './Sections/Section02Contact';
import Section03Address from './Sections/Section03Address';
import Section04Education from './Sections/Section04Education';
import Section05Employment from './Sections/Section05Employment';
import Section06Skills from './Sections/Section06Skills';
import Section07Preferences from './Sections/Section07Preferences';
import Section08Documents from './Sections/Section08Documents';
import Section09CertificateConfirmation from './Sections/Section09CertificateConfirmation';
import Section10CertificateDelivery from './Sections/Section10CertificateDelivery';
import Section11Marketing from './Sections/Section11Marketing';
import Section12Declaration from './Sections/Section12Declaration';

// API
import {
  getStudentProfile,
  saveStudentProfileDraft,
  submitStudentProfile,
  confirmCertificateDelivery
} from '../../../Constants/Api/functions/function';

import './style.scss';

const { Step } = Steps;

const STEPS = [
  { title: 'Personal & Contact', icon: <UserOutlined />, desc: 'Sections 01 – 02' },
  { title: 'Address & Education', icon: <HomeOutlined />, desc: 'Sections 03 – 04' },
  { title: 'Career & Interests', icon: <SolutionOutlined />, desc: 'Sections 05 – 07' },
  { title: 'Documents', icon: <FileProtectOutlined />, desc: 'Section 08' },
  { title: 'Certificate & Delivery', icon: <SafetyCertificateOutlined />, desc: 'Sections 09 – 10' },
  { title: 'Preferences & Submit', icon: <CheckCircleOutlined />, desc: 'Sections 11 – 12' },
];

const StudentProfile = () => {
  const [form] = Form.useForm();
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const userId = userProfile?.id;

  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Loaded Profile Data
  const [profileData, setProfileData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [academicSnapshot, setAcademicSnapshot] = useState([]);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [certNameValue, setCertNameValue] = useState('');
  const [completionPercentage, setCompletionPercentage] = useState(0);

  const calculateProgress = useCallback((values = {}, docs = [], status = '', photo = null) => {
    if (['verified', 'submitted', 'under_review'].includes(status)) {
      return 100;
    }

    let score = 0;
    const totalMax = 100;

    // 1. Personal & Contact Info (25 pts)
    if (values.fullName && String(values.fullName).trim()) score += 4;
    if (values.certificateName && String(values.certificateName).trim()) score += 5;
    if (values.certificateNameConfirmed) score += 3;
    if (values.nicPassport && String(values.nicPassport).trim()) score += 4;
    if (values.dateOfBirth) score += 2;
    if (values.primaryContact && String(values.primaryContact).trim()) score += 3;
    if (values.whatsappNumber && String(values.whatsappNumber).trim()) score += 2;
    if (photo || values.profilePhotoUrl) score += 2;

    // 2. Address & Education (20 pts)
    if (values.permanentAddress && String(values.permanentAddress).trim()) score += 5;
    if (values.province) score += 3;
    if (values.district) score += 3;
    if (values.city && String(values.city).trim()) score += 3;
    if (values.highestEducation) score += 3;
    if (values.instituteName && String(values.instituteName).trim()) score += 3;

    // 3. Career, Skills & Preferences (15 pts)
    if (values.employmentStatus) score += 5;
    if (Array.isArray(values.interestedAreas) && values.interestedAreas.length > 0) score += 5;
    if (values.preferredLearningMode) score += 5;

    // 4. Verification Documents (20 pts)
    const docTypes = (docs || []).map((d) => d.documentType);
    if (docTypes.includes('nic_front')) score += 7;
    if (docTypes.includes('nic_back')) score += 7;
    if (docTypes.includes('passport_photo')) score += 6;

    // 5. Certificate Delivery (10 pts)
    if (values.deliveryMethod) score += 5;
    if (values.recipientName && String(values.recipientName).trim()) score += 3;
    if (values.deliveryContact && String(values.deliveryContact).trim()) score += 2;

    // 6. Declarations (10 pts)
    if (values.declarationAccurate) score += 2;
    if (values.declarationGenuine) score += 2;
    if (values.declarationAuthorizeVerification) score += 2;
    if (values.declarationCertNameConfirmed) score += 2;
    if (values.declarationDeliveryConfirmed) score += 2;

    return Math.min(Math.round((score / totalMax) * 100), 100);
  }, []);

  const fetchProfile = async () => {
    if (!userId) return;
    try {
      setIsLoading(true);
      const res = await getStudentProfile(userId);
      if (res && res.success) {
        setProfileData(res.profile);
        setDocuments(res.documents || []);
        setAcademicSnapshot(res.academicSnapshot || []);

        if (res.profile) {
          const p = res.profile;
          const photoUrl = p.profilePhotoUrl || userProfile?.profileImage || null;
          setProfilePhoto(photoUrl);
          setCertNameValue(p.certificateName || userProfile?.name || '');

          const populatedValues = {
            ...p,
            dateOfBirth: p.dateOfBirth ? dayjs(p.dateOfBirth) : null,
            certificateNameConfirmed: Boolean(p.certificateNameConfirmed),
            isCurrentSameAsPermanent: Boolean(p.isCurrentSameAsPermanent),
            receiveCourseUpdates: Boolean(p.receiveCourseUpdates),
            receiveSpecialOffers: Boolean(p.receiveSpecialOffers),
            receiveWhatsappUpdates: Boolean(p.receiveWhatsappUpdates),
            receiveEmailUpdates: Boolean(p.receiveEmailUpdates),
            declarationAccurate: Boolean(p.declarationAccurate),
            declarationGenuine: Boolean(p.declarationGenuine),
            declarationAuthorizeVerification: Boolean(p.declarationAuthorizeVerification),
            declarationCertNameConfirmed: Boolean(p.declarationCertNameConfirmed),
            declarationDeliveryConfirmed: Boolean(p.declarationDeliveryConfirmed),
          };

          form.setFieldsValue(populatedValues);
          setCompletionPercentage(
            calculateProgress(
              populatedValues,
              res.documents || [],
              p.submissionStatus || '',
              photoUrl
            )
          );
        } else {
          // Pre-populate with initial user auth record
          const initialName = userProfile?.name || '';
          const initialPhoto = userProfile?.profileImage || null;
          setCertNameValue(initialName);
          setProfilePhoto(initialPhoto);

          const defaultValues = {
            fullName: initialName,
            certificateName: initialName,
            email: userProfile?.email || '',
            primaryContact: userProfile?.contactNumber || '',
            whatsappNumber: userProfile?.contactNumber || '',
            permanentAddress: userProfile?.address || '',
            recipientName: initialName,
            deliveryContact: userProfile?.contactNumber || '',
            deliveryEmail: userProfile?.email || '',
          };

          form.setFieldsValue(defaultValues);
          setCompletionPercentage(
            calculateProgress(
              defaultValues,
              res.documents || [],
              '',
              initialPhoto
            )
          );
        }
      }
    } catch (err) {
      console.error('Fetch profile error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Recalculate progress when documents, photo, profile status, or current step change
  useEffect(() => {
    const currentValues = form.getFieldsValue();
    const currentStatus = profileData?.submissionStatus || '';
    const currentPhoto = profilePhoto || currentValues.profilePhotoUrl;
    setCompletionPercentage(calculateProgress(currentValues, documents, currentStatus, currentPhoto));
  }, [documents, profilePhoto, profileData, currentStep, calculateProgress, form]);

  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      const currentValues = form.getFieldsValue();
      const payload = {
        ...currentValues,
        userId,
        dateOfBirth: currentValues.dateOfBirth
          ? (typeof currentValues.dateOfBirth.format === 'function'
              ? currentValues.dateOfBirth.format('YYYY-MM-DD')
              : String(currentValues.dateOfBirth).slice(0, 10))
          : null,
        profilePhotoUrl: profilePhoto || currentValues.profilePhotoUrl,
      };

      const res = await saveStudentProfileDraft(payload);
      if (res && res.success) {
        message.success('Progress saved as draft.');
      } else {
        message.error(res?.message || 'Failed to save draft');
      }
    } catch (err) {
      console.error('Save draft error:', err);
      message.error('An error occurred while saving draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleNextStep = async () => {
    try {
      // Validate current step fields before progressing
      if (currentStep === 0) {
        await form.validateFields([
          'fullName',
          'certificateName',
          'certificateNameConfirmed',
          'dateOfBirth',
          'nicPassport',
          'primaryContact',
          'whatsappNumber',
          'email',
          'preferredCommunication',
        ]);
      } else if (currentStep === 1) {
        await form.validateFields([
          'permanentAddress',
          'currentAddress',
          'province',
          'district',
          'city',
          'highestEducation',
          'instituteName',
        ]);
      } else if (currentStep === 2) {
        await form.validateFields([
          'employmentStatus',
          'interestedAreas',
          'skillsToDevelop',
          'preferredLearningMode',
        ]);
      }

      // Auto-save draft silently
      const currentValues = form.getFieldsValue();
      saveStudentProfileDraft({
        ...currentValues,
        userId,
        dateOfBirth: currentValues.dateOfBirth
          ? (typeof currentValues.dateOfBirth.format === 'function'
              ? currentValues.dateOfBirth.format('YYYY-MM-DD')
              : String(currentValues.dateOfBirth).slice(0, 10))
          : null,
        profilePhotoUrl: profilePhoto || currentValues.profilePhotoUrl,
      });

      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.warn('Validation incomplete for step:', err);
      message.warning('Please complete the required fields in this step before proceeding.');
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmitProfile = async () => {
    try {
      // Validate all required fields
      try {
        await form.validateFields();
      } catch (validationErr) {
        if (validationErr.errorFields && validationErr.errorFields.length > 0) {
          const firstErr = validationErr.errorFields[0];
          const fieldName = Array.isArray(firstErr.name) ? firstErr.name[0] : firstErr.name;
          const step0Fields = ['fullName', 'certificateName', 'certificateNameConfirmed', 'dateOfBirth', 'gender', 'nicPassport', 'primaryContact', 'whatsappNumber', 'email', 'preferredCommunication'];
          const step1Fields = ['permanentAddress', 'currentAddress', 'province', 'district', 'city', 'highestEducation', 'instituteName'];
          const step2Fields = ['employmentStatus', 'interestedAreas', 'skillsToDevelop', 'preferredLearningMode'];
          const step4Fields = ['deliveryMethod', 'deliveryAddress', 'nearestCity', 'district', 'deliveryDistrict', 'recipientName', 'deliveryContact'];
          const step5Fields = ['declarationAccurate', 'declarationGenuine', 'declarationAuthorizeVerification', 'declarationCertNameConfirmed', 'declarationDeliveryConfirmed'];

          if (step0Fields.includes(fieldName)) setCurrentStep(0);
          else if (step1Fields.includes(fieldName)) setCurrentStep(1);
          else if (step2Fields.includes(fieldName)) setCurrentStep(2);
          else if (step4Fields.includes(fieldName)) setCurrentStep(4);
          else if (step5Fields.includes(fieldName)) setCurrentStep(5);

          message.error(firstErr.errors[0] || 'Please complete all required fields.');
          return;
        }
      }

      // Check required documents
      const hasNicFront = documents.some((d) => d.documentType === 'nic_front');
      const hasNicBack = documents.some((d) => d.documentType === 'nic_back');
      const hasPhoto = documents.some((d) => d.documentType === 'passport_photo');

      if (!hasNicFront || !hasNicBack || !hasPhoto) {
        message.error('Please upload your NIC Front, NIC Back, and Passport Photograph in Step 4 before submitting.');
        setCurrentStep(3); // Jump to Step 4 Documents
        return;
      }

      setIsSubmitting(true);
      const allValues = form.getFieldsValue();
      const payload = {
        ...allValues,
        userId,
        dateOfBirth: allValues.dateOfBirth
          ? (typeof allValues.dateOfBirth.format === 'function'
              ? allValues.dateOfBirth.format('YYYY-MM-DD')
              : String(allValues.dateOfBirth).slice(0, 10))
          : null,
        profilePhotoUrl: profilePhoto || allValues.profilePhotoUrl,
      };

      const res = await submitStudentProfile(payload);
      if (res && res.success) {
        if (notification && typeof notification.destroy === 'function') {
          notification.destroy('student-profile-incomplete-notification');
        }
        // Also save certificate delivery confirmation if courses exist
        if (academicSnapshot.length > 0) {
          for (const course of academicSnapshot) {
            await confirmCertificateDelivery({
              userId,
              courseId: course.courseId,
              certificateId: course.certificateId,
              studentRegNo: course.studentRegNo,
              courseName: course.courseName,
              completionDate: course.completionDate,
              marks: course.marks,
              grade: course.grade,
              certificateNumber: course.certificateNumber,
              confirmedCertificateName: allValues.certificateName,
              deliveryMethod: allValues.deliveryMethod || 'courier',
              deliveryAddress: allValues.deliveryAddress || allValues.permanentAddress,
              nearestCity: allValues.nearestCity || allValues.city,
              district: allValues.deliveryDistrict || allValues.district,
              postalCode: allValues.deliveryPostalCode || allValues.postalCode,
              recipientName: allValues.recipientName || allValues.fullName,
              deliveryContact: allValues.deliveryContact || allValues.primaryContact,
              whatsappNumber: allValues.deliveryWhatsapp || allValues.whatsappNumber,
              emailAddress: allValues.deliveryEmail || allValues.email,
              specialInstructions: allValues.specialInstructions,
            });
          }
        }

        Modal.success({
          title: 'Profile Submitted Successfully!',
          content: (
            <div>
              <p>Your official student profile and verification documents have been submitted to the IBAS Academic Division.</p>
              <p>Our verification officers will review your credentials within 2–3 working days. You can track the status of your documents anytime on this portal.</p>
            </div>
          ),
          onOk: () => {
            fetchProfile();
          },
        });
      } else {
        message.error(res?.message || 'Failed to submit profile');
      }
    } catch (err) {
      console.error('Submit profile error:', err);
      message.error('Please complete all required fields and accept all declarations.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = () => {
    const status = profileData?.submissionStatus || 'draft';
    switch (status) {
      case 'verified':
        return <Tag color="success" className="status-tag">Profile Verified</Tag>;
      case 'submitted':
        return <Tag color="processing" className="status-tag">Under Review</Tag>;
      case 'action_required':
        return <Tag color="error" className="status-tag">Action Required</Tag>;
      case 'draft':
      default:
        return <Tag color="warning" className="status-tag">Draft / Incomplete</Tag>;
    }
  };

  // Only students can fill this form; redirect staff/admins to verifications dashboard
  if (userProfile?.role && userProfile?.role !== 'student') {
    if (['superadmin', 'admin', 'coordinator'].includes(userProfile.role)) {
      return <Redirect to="/dashboard/student-verifications" />;
    }
    return <Redirect to="/dashboard/home" />;
  }

  return (
    <div className="student-profile-page">
      {/* Top Banner Header */}
      <div className="profile-hero-header">
        <div className="hero-content">
          <div className="hero-title-group">
            <h2>Student Profile & Certificate Verification</h2>
            <p>
              Official student registration, document authentication, and certificate dispatch coordination portal for IBAS.
            </p>
          </div>
          <div className="hero-status-group">
            <span className="status-label">Verification Status:</span>
            {getStatusBadge()}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="hero-progress-bar">
          <div className="progress-labels">
            <span>Profile Completion</span>
            <strong>{completionPercentage}%</strong>
          </div>
          <Progress
            percent={completionPercentage}
            showInfo={false}
            strokeColor={{ '0%': '#38bdf8', '100%': '#10b981' }}
          />
        </div>
      </div>

      {/* Rejection / Action Required Notice */}
      {profileData?.submissionStatus === 'action_required' && (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message="Action Required On Your Verification Profile"
          description={profileData.rejectionNotes || 'One or more of your documents were rejected. Please review Section 08 and re-upload the required files.'}
          style={{ marginBottom: 24, borderRadius: 10 }}
        />
      )}

      {/* Stepper Navigation for Desktop / Tablet */}
      <div className="steps-container">
        <Steps current={currentStep} onChange={(step) => setCurrentStep(step)}>
          {STEPS.map((s, i) => (
            <Step key={i} title={s.title} icon={s.icon} description={s.desc} />
          ))}
        </Steps>
      </div>

      {/* Stepper Navigation for Mobile Screens */}
      <div className="mobile-step-indicator-bar">
        <div className="mobile-step-info">
          <span className="step-count">STEP {currentStep + 1} OF {STEPS.length}</span>
          <strong className="step-current-title">{STEPS[currentStep].title}</strong>
          <span className="step-sections-hint">{STEPS[currentStep].desc}</span>
        </div>
        <div className="mobile-step-dots">
          {STEPS.map((s, idx) => (
            <button
              key={idx}
              type="button"
              className={`mobile-dot ${idx === currentStep ? 'active' : idx < currentStep ? 'completed' : ''}`}
              onClick={() => setCurrentStep(idx)}
              aria-label={`Go to step ${idx + 1}: ${s.title}`}
            />
          ))}
        </div>
      </div>

      {/* Main Form Content */}
      <Spin spinning={isLoading} tip="Loading your profile data...">
        <Form
          form={form}
          layout="vertical"
          preserve={true}
          onValuesChange={(changed, allValues) => {
            if (changed.certificateName !== undefined) {
              setCertNameValue(changed.certificateName);
            }
            const currentStatus = profileData?.submissionStatus || '';
            const currentPhoto = profilePhoto || allValues.profilePhotoUrl;
            setCompletionPercentage(calculateProgress(allValues, documents, currentStatus, currentPhoto));
          }}
          className="profile-form-wrapper"
        >
          {/* Step 1: Personal & Contact */}
          <div className="step-pane fade-in" style={{ display: currentStep === 0 ? 'block' : 'none' }}>
            <Section01Personal
              form={form}
              profilePhoto={profilePhoto}
              setProfilePhoto={setProfilePhoto}
              userId={userId}
            />
            <div className="section-divider-line" />
            <Section02Contact />
          </div>

          {/* Step 2: Address & Education */}
          <div className="step-pane fade-in" style={{ display: currentStep === 1 ? 'block' : 'none' }}>
            <Section03Address form={form} />
            <div className="section-divider-line" />
            <Section04Education />
          </div>

          {/* Step 3: Career, Skills & Learning */}
          <div className="step-pane fade-in" style={{ display: currentStep === 2 ? 'block' : 'none' }}>
            <Section05Employment />
            <div className="section-divider-line" />
            <Section06Skills />
            <div className="section-divider-line" />
            <Section07Preferences />
          </div>

          {/* Step 4: Verification Documents */}
          <div className="step-pane fade-in" style={{ display: currentStep === 3 ? 'block' : 'none' }}>
            <Section08Documents
              userId={userId}
              documents={documents}
              onDocumentUploaded={fetchProfile}
            />
          </div>

          {/* Step 5: Certificate & Delivery */}
          <div className="step-pane fade-in" style={{ display: currentStep === 4 ? 'block' : 'none' }}>
            <Section09CertificateConfirmation
              academicSnapshot={academicSnapshot}
              certificateName={certNameValue}
            />
            <div className="section-divider-line" />
            <Section10CertificateDelivery form={form} />
          </div>

          {/* Step 6: Marketing & Final Declaration */}
          <div className="step-pane fade-in" style={{ display: currentStep === 5 ? 'block' : 'none' }}>
            <Section11Marketing />
            <div className="section-divider-line" />
            <Section12Declaration />
          </div>

          {/* Navigation Controls Bar */}
          <div className="stepper-navigation-bar">
            <div className="left-nav-actions">
              {currentStep > 0 && (
                <Button size="large" icon={<ArrowLeftOutlined />} onClick={handlePrevStep} className="btn-step-prev">
                  Previous Step
                </Button>
              )}
            </div>

            <div className="right-nav-actions">
              <Button
                size="large"
                icon={<SaveOutlined />}
                onClick={handleSaveDraft}
                loading={isSavingDraft}
                className="btn-save-draft"
              >
                Save Draft
              </Button>

              {currentStep < STEPS.length - 1 ? (
                <Button type="primary" size="large" onClick={handleNextStep} className="btn-step-next">
                  Continue to Next Step <ArrowRightOutlined />
                </Button>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  onClick={handleSubmitProfile}
                  loading={isSubmitting}
                  className="btn-final-submit"
                >
                  Submit Profile
                </Button>
              )}
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default StudentProfile;
